/**
 * Workbench Prescriptive next-action projection adapter V0.
 * Binds Design Gate façade → surface view-model. Preview/admission only.
 * No ControlledActionPort invoke · ASSISTIVE_AI_LIVE remains false.
 */
import type { AiOrchestrationProposalV0 } from '../ai-orchestration/ControlledBusinessAiOrchestrationContract';
import type { RiskClassV0 } from '../system-governed-execution-authority/contracts/SystemGovernedExecutionAuthorityContractsV0';
import {
  SURFACE_GRAMMAR_VERBS_V0,
  assembleWorkbenchNextActionContextV0,
  buildPreviewProposalReceiptProjectionV0,
  canAiInvokeControlledExecutionV0,
  isV0ControlledActionKeyV0,
  type AiSurfaceVerbV0,
  type ProductAdmissionUxLabelV0,
  type ProvenanceClassV0,
} from './AiOrchestrationSurfaceContractsV0';

export type WorkbenchNextActionPreviewVmV0 = {
  contextPurpose: string;
  surfacePlane: 'business';
  surfaceSlot: 'Prescriptive';
  taskId: string;
  tenantId: string;
  proposalId: string;
  recommendationSummary: string;
  suggestedActionKey: string;
  suggestedActionInCatalog: boolean;
  proposalStatus: 'PENDING';
  uxAdmission: ProductAdmissionUxLabelV0;
  provenance: ProvenanceClassV0;
  riskClass: RiskClassV0;
  sgeaDecision: 'DEFER';
  grantsAuthority: false;
  businessMutationDelta: 0;
  controlledActionPortInvokes: 0;
  assistiveAiLiveEnabled: false;
  executionAllowed: false;
  executionDenyReason: string;
  evidenceRefs: readonly string[];
  verbs: readonly AiSurfaceVerbV0[];
  disclaimer: string;
};

const DEFAULT_SUMMARY =
  'Review the next bounded workbench action. Operator approval is required; AI cannot execute.';

export function buildWorkbenchNextActionPendingProposalV0(input: {
  tenantId: string;
  taskId: string;
  evidenceRefs: readonly string[];
  recommendationSummary?: string;
  suggestedActionKey?: string;
  proposalId?: string;
}): AiOrchestrationProposalV0 {
  return {
    proposalId: input.proposalId ?? `prop_wb_next_${input.taskId}`,
    tenantId: input.tenantId,
    queueItemId: `qi_${input.taskId}`,
    sourceDomain: 'workbench',
    sourceItemId: input.taskId,
    recommendationSummary: input.recommendationSummary ?? DEFAULT_SUMMARY,
    evidenceRefs: input.evidenceRefs,
    suggestedActionKey: input.suggestedActionKey ?? 'task.advance',
    status: 'PENDING',
    createdAt: '1970-01-01T00:00:00.000Z',
  };
}

/**
 * Pure projection: Task context → context envelope → PENDING proposal → preview receipt VM.
 * Representative wiring lane — CAP invoke count stays 0.
 */
export function projectWorkbenchNextActionPreviewV0(input: {
  tenantId: string;
  requestingActorId: string;
  taskId: string;
  evidenceRefs?: readonly string[];
  recommendationSummary?: string;
  suggestedActionKey?: string;
  correlationId?: string;
}): WorkbenchNextActionPreviewVmV0 {
  const evidenceRefs = input.evidenceRefs ?? [`ev_task_${input.taskId}`];
  const ctx = assembleWorkbenchNextActionContextV0({
    tenantId: input.tenantId,
    requestingActorId: input.requestingActorId,
    taskId: input.taskId,
    evidenceRefs,
    correlationId: input.correlationId,
  });

  const proposal = buildWorkbenchNextActionPendingProposalV0({
    tenantId: ctx.tenantId,
    taskId: input.taskId,
    evidenceRefs: ctx.evidenceRefs,
    recommendationSummary: input.recommendationSummary,
    suggestedActionKey: input.suggestedActionKey,
  });

  const receipt = buildPreviewProposalReceiptProjectionV0({
    proposal,
    riskClass: 'R1',
    sgeaDecision: 'DEFER',
    decisionReceiptId: `rcpt_preview_${proposal.proposalId}`,
    provenance: 'PROPOSAL',
  });

  const invoke = canAiInvokeControlledExecutionV0({
    actorKind: 'AI',
    sgeaDecision: 'ALLOW',
    proposalStatus: proposal.status,
    assistiveAiLiveEnabled: false,
  });

  return {
    contextPurpose: ctx.purpose,
    surfacePlane: 'business',
    surfaceSlot: 'Prescriptive',
    taskId: input.taskId,
    tenantId: ctx.tenantId,
    proposalId: proposal.proposalId,
    recommendationSummary: proposal.recommendationSummary,
    suggestedActionKey: proposal.suggestedActionKey,
    suggestedActionInCatalog: isV0ControlledActionKeyV0(proposal.suggestedActionKey),
    proposalStatus: 'PENDING',
    uxAdmission: receipt.uxAdmission,
    provenance: receipt.provenance,
    riskClass: receipt.riskClass,
    sgeaDecision: 'DEFER',
    grantsAuthority: false,
    businessMutationDelta: 0,
    controlledActionPortInvokes: 0,
    assistiveAiLiveEnabled: false,
    executionAllowed: false,
    executionDenyReason: invoke.reason ?? 'AI_DIRECT_EXECUTE_DENIED',
    evidenceRefs: ctx.evidenceRefs,
    verbs: SURFACE_GRAMMAR_VERBS_V0,
    disclaimer:
      'AI proposal ≠ admission. Preview card ≠ DecisionReceipt. Suggested action ≠ authorized execution.',
  };
}
